import React, { useEffect, useMemo, useState } from 'react';
import { getAuthToken } from '../../auth/context/AuthContext';
import WalletCard, { InvoiceSheet } from '../components/WalletCard';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/admin`;

const STATUS_LABEL = {
  initiated: 'Initiated',
  success: 'Success',
  failed: 'Failed',
  canceled: 'Canceled',
};

function createdDay(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

function createdLabel(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

export default function RechargeOrders() {
  const [orders, setOrders] = useState(null);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailErr, setDetailErr] = useState('');
  const [downloading, setDownloading] = useState(false);

  const loadOrders = async () => {
    const res = await fetch(`${API}/wallet/orders`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not load orders');
    setOrders(data.orders || []);
  };

  useEffect(() => {
    let gone = false;
    loadOrders().catch((e) => { if (!gone) setErr(e.message); });
    return () => { gone = true; };
  }, []);

  const openDetail = async (ledgerId) => {
    setDetailErr('');
    setDetail({ loading: true, id: ledgerId });
    try {
      const res = await fetch(`${API}/wallet/transactions/${ledgerId}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not load this payment');
      setDetail(data);
    } catch (e) {
      setDetail(null);
      setDetailErr(e.message);
    }
  };

  const downloadInvoice = async (row) => {
    setDownloading(true);
    setDetailErr('');
    try {
      const res = await fetch(`${API}/wallet/transactions/${row.id}/invoice`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not download the invoice');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fromHeader = (res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/);
      const fromNumber = (row?.invoice?.number || '').replace(/^#/, '').replace(/[^A-Za-z0-9_-]/g, '');
      link.href = url;
      link.download = fromNumber ? `${fromNumber}.pdf` : ((fromHeader && fromHeader[1]) || 'invoice.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDetailErr(e.message);
    }
    setDownloading(false);
  };

  const rangeInvalid = Boolean(startDate && endDate && startDate > endDate);
  const filtered = useMemo(() => {
    if (!orders || rangeInvalid) return [];
    return orders.filter(order => {
      if (status && order.status !== status) return false;
      const day = createdDay(order.created_at);
      if ((startDate || endDate) && !day) return false;
      if (startDate && day < startDate) return false;
      if (endDate && day > endDate) return false;
      return true;
    });
  }, [orders, status, startDate, endDate, rangeInvalid]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Manage Wallet</div>
          <div className="page-subtitle">
            Balance, add money, and each top-up from the moment payment starts.
          </div>
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <WalletCard onOrdersChange={() => { loadOrders().catch((e) => setErr(e.message)); }} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>
        Recharge orders
      </div>
      {err && <div className="alert alert-error">{err}</div>}
      {orders === null && !err && <div className="empty-state-text">Loading…</div>}
      {orders && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <label>
              <div className="form-label">Status</div>
              <select className="form-input" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All</option>
                <option value="initiated">Initiated</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
                <option value="canceled">Canceled</option>
              </select>
            </label>
            <label>
              <div className="form-label">From</div>
              <input className="form-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label>
              <div className="form-label">To</div>
              <input className="form-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
          </div>
          {rangeInvalid && (
            <div style={{ fontSize: 12, color: 'var(--danger, #b91c1c)', marginTop: 8 }}>
              From is after To.
            </div>
          )}
        </div>
      )}
      {orders && orders.length === 0 && (
        <div className="card" style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)' }}>
          No recharge orders yet.
        </div>
      )}
      {orders && orders.length > 0 && !rangeInvalid && filtered.length === 0 && (
        <div className="card" style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)' }}>
          No orders match these filters.
        </div>
      )}
      {filtered.length > 0 && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Message</th>
                <th>View detail</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(order => (
                <tr key={order.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{createdLabel(order.created_at)}</td>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{order.amount_display}</td>
                  <td>{STATUS_LABEL[order.status] || order.status}</td>
                  <td>{order.message || 'Online'}</td>
                  <td>
                    {order.status === 'success' && order.ledger_id ? (
                      <button type="button" className="btn btn-sm" onClick={() => openDetail(order.ledger_id)}>
                        View detail
                      </button>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detailErr && <div className="alert alert-error" style={{ marginTop: 12 }}>{detailErr}</div>}
      {detail && (
        <div className="modal-backdrop" onClick={() => setDetail(null)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
            {detail.loading ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
            ) : (
              <>
                {detail.invoice ? <InvoiceSheet invoice={detail.invoice} /> : (
                  <div style={{ fontSize: 13 }}>This payment has no invoice.</div>
                )}
                <div className="modal-actions">
                  {detail.has_invoice && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={downloading}
                      onClick={() => downloadInvoice(detail)}
                    >
                      {downloading ? 'Downloading…' : 'Download invoice'}
                    </button>
                  )}
                  <button type="button" className="btn" onClick={() => setDetail(null)}>Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
